import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Token ${token}` }),
  };
};

const getApiBaseUrl = () =>
  process.env.REACT_APP_API_URL || "http://localhost:8000";

const SALARY_TYPE_OPTIONS = [
  { value: "hourly", label: "Per Hour" },
  { value: "monthly", label: "Per Month" },
];

const emptySalary = {
  salary_type: "hourly",
  amount: "",
  start_date: new Date().toISOString().split("T")[0],
  end_date: "",
  notes: "",
};

export default function SalariesTab() {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [formData, setFormData] = useState({ ...emptySalary });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [salaryPage, setSalaryPage] = useState(0);
  const PAGE_SIZE = 5;

  // Load staff list on mount
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/staff/`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setStaffList(data);
        }
      } catch (e) {
        console.error("Failed to load staff", e);
      }
    };
    loadStaff();
  }, []);

  // Load salary records when staff selection changes
  const loadRecords = useCallback(async () => {
    if (!selectedStaffId) {
      setSalaryRecords([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/staff/salary-records/?staff_id=${selectedStaffId}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        setSalaryRecords(await res.json());
      }
    } catch (e) {
      console.error("Failed to load salary records", e);
    } finally {
      setLoading(false);
    }
  }, [selectedStaffId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const resetForm = () => {
    setFormData({ ...emptySalary });
    setEditingId(null);
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    if (!selectedStaffId) {
      setError("Please select an employee first.");
      return;
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      setError("Please enter a valid amount.");
      return;
    }
    if (!formData.start_date) {
      setError("Start date is required.");
      return;
    }

    const payload = {
      staff: selectedStaffId,
      salary_type: formData.salary_type,
      amount: formData.amount,
      start_date: formData.start_date,
      end_date: formData.end_date || null,
      notes: formData.notes || "",
    };

    try {
      const url = editingId
        ? `${getApiBaseUrl()}/api/staff/salary-records/${editingId}/`
        : `${getApiBaseUrl()}/api/staff/salary-records/`;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSuccess(editingId ? "Salary record updated." : "Salary record created.");
        resetForm();
        loadRecords();
      } else {
        const data = await res.json();
        setError(data.detail || JSON.stringify(data));
      }
    } catch (e) {
      setError("Network error while saving salary record.");
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setFormData({
      salary_type: record.salary_type,
      amount: record.amount,
      start_date: record.start_date,
      end_date: record.end_date || "",
      notes: record.notes || "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this salary record?")) return;
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/staff/salary-records/${id}/`,
        { method: "DELETE", headers: getAuthHeaders() }
      );
      if (res.ok || res.status === 204) {
        loadRecords();
      }
    } catch (e) {
      console.error("Delete failed", e);
    }
  };

  const staffName = (s) =>
    `${s.user_details?.first_name || ""} ${s.user_details?.last_name || ""}`.trim() ||
    s.user_details?.username ||
    `Staff #${s.id}`;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Salary Management
      </Typography>

      {/* Employee selector */}
      <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }}>
        <InputLabel>Employee</InputLabel>
        <Select
          value={selectedStaffId}
          onChange={(e) => {
            setSelectedStaffId(e.target.value);
            resetForm();
            setSalaryPage(0);
          }}
          label="Employee"
        >
          {staffList.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {staffName(s)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedStaffId && (
        <>
          {/* Add / Edit form */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {editingId ? "Edit Salary Record" : "Add New Salary Record"}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}
            >
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.salary_type}
                  onChange={handleChange("salary_type")}
                  label="Type"
                  size="small"
                >
                  {SALARY_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Amount (€)"
                type="number"
                size="small"
                value={formData.amount}
                onChange={handleChange("amount")}
                inputProps={{ step: "0.01", min: "0" }}
                sx={{ width: 140 }}
              />

              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={formData.start_date}
                onChange={handleChange("start_date")}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />

              <TextField
                label="End Date"
                type="date"
                size="small"
                value={formData.end_date}
                onChange={handleChange("end_date")}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
                helperText="Optional"
              />

              <TextField
                label="Notes"
                size="small"
                value={formData.notes}
                onChange={handleChange("notes")}
                sx={{ minWidth: 180, flex: 1 }}
              />
            </Box>

            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button variant="contained" onClick={handleSave}>
                {editingId ? "Update" : "Add Salary"}
              </Button>
              {editingId && (
                <Button variant="outlined" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>

          {/* Salary history table */}
          {loading ? (
            <CircularProgress />
          ) : salaryRecords.length === 0 ? (
            <Typography color="text.secondary">
              No salary records for this employee.
            </Typography>
          ) : (
            <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount (€)</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salaryRecords.slice(salaryPage * PAGE_SIZE, (salaryPage + 1) * PAGE_SIZE).map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Chip
                        label={r.salary_type === "hourly" ? "Per Hour" : "Per Month"}
                        size="small"
                        color={r.salary_type === "hourly" ? "primary" : "secondary"}
                      />
                    </TableCell>
                    <TableCell>
                      {Number(r.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>{r.start_date}</TableCell>
                    <TableCell>{r.end_date || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={r.is_current ? "Current" : "Expired"}
                        color={r.is_current ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{r.notes || "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(r)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(r.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={salaryRecords.length}
              page={salaryPage}
              onPageChange={(e, newPage) => setSalaryPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[5]}
            />
            </>
          )}
        </>
      )}
    </Box>
  );
}
